import { Component, Inject, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DOCUMENT } from "@angular/common";
import { HealthSystemService } from '../core/services/v2/health-system.service';
import { PDFDialogComponent } from '../signup-organization/pdf-dialog/pdf-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { HealthOrganizationTypesEnum } from '../core/enums/health-organization-types.enum';

@Component({
  selector: 'signup-organization-new',
  templateUrl: './signup-organization-new.component.html',
  styleUrls: ['./signup-organization-new.component.scss']
})
export class SignupOrganizationNewComponent implements OnInit, OnDestroy, AfterViewInit {

  organizationForm: FormGroup;
  contactForm: FormGroup;
  subscriptionForm: FormGroup;
  stepNo = 0;
  healthOrgTypes: any[] = [];
  healthOrgSubTypes: any[] = [];
  isDisplayThankYou = false;
  isDataLoaded = false;
  isFormSubmitting: boolean = false;
  signupType: number = null;
  signupTypeLable = 'Organization';
  isBrokerAgent = false;
  isEmployer = false;
  get healthOrganizationTypes() { return HealthOrganizationTypesEnum; }

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private healthSystemService: HealthSystemService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    this.signupType = this.activatedRoute.snapshot.queryParams['signuptype'] || null;
      if (this.signupType) {
      this.signupTypeLable = this.healthOrganizationTypes.BrokerAgent === Number(this.signupType) ? 'Broker Agents' : 'Organization';
      this.isBrokerAgent = this.healthOrganizationTypes.BrokerAgent === Number(this.signupType);
      this.isEmployer = this.healthOrganizationTypes.Employers === Number(this.signupType);

      if (this.isBrokerAgent) {
        this.organizationForm.controls.phoneNumber.setValidators([Validators.required, Validators.maxLength(14), Validators.pattern("^[0-9]*$")]);
        this.organizationForm.controls.phoneNumber.updateValueAndValidity();
      } else if (this.isEmployer) {
        this.signupTypeLable = 'Employers';
        this.isEmployer = true;
        this.organizationForm.controls.phoneNumber.setValidators([Validators.required, Validators.maxLength(14), Validators.pattern("^[0-9]*$")]);
        this.organizationForm.controls.phoneNumber.updateValueAndValidity();
      } else {
        this.organizationForm.controls.name.setValidators(Validators.required);
        this.organizationForm.controls.name.updateValueAndValidity();

        this.organizationForm.controls.phoneNumber.setValidators(Validators.required);
        this.organizationForm.controls.phoneNumber.updateValueAndValidity();

        this.organizationForm.controls.webAddress.setValidators([Validators.required,
        Validators.pattern('^((http|https)?:\/\/)?(www\.)?[a-zA-Z0-9-\.]+\.[a-z]{2,4}$')]);
        this.organizationForm.controls.webAddress.updateValueAndValidity();
      }
      this.loadData();
    } else {
      this.router.navigate(['/home']);
    }
  }

  ngOnDestroy(): void {
    this.toggleBodyClass();
  }

  ngAfterViewInit(): void {
    this.toggleBodyClass();
  }

  toggleBodyClass(): void {
    document.querySelector('body').classList.toggle('sign-up-container');
  }

  initializeForms(): void {
    this.organizationForm = this.fb.group({
      organizationType: ['', Validators.required],
      organizationName: [{ value: '', disabled: true }, [Validators.required]],
      name: [''],
      phoneNumber: [''],
      webAddress: [''],
      npnNumber: [''],
      networkProvider: ['None'],
      primaryAddress: ['', Validators.required],
      buildingSuite: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(5), Validators.pattern("^[0-9]*$")]],
      firstName: ['', Validators.required],
      middleInitial: [''],
      lastName: ['', Validators.required],
      suffix: [''],
      email: ['', Validators.required],
    });

    this.contactForm = this.fb.group({
      saaSAgreement: ['', Validators.required],
      isAcceptedBaa: ['', Validators.required],
      creditCardDisclaimer: [''],
      moneyBackGuarantee: [''],
      cancelationPolicy: [''],
      noticeWebsitePrivacyPractices: [''],
      copyrightNotice: [''],
      privacyPolicy: [''],
      medicalInformationDisclaimer: [''],
      noticeHealthcarePractices: [''],
      privacySecurityElectronicHR: [''],
      isCertify: ['', Validators.required],
    });

    this.subscriptionForm = this.fb.group({
      subscriptionType: ['', Validators.required],
    });

  }

  async loadData(): Promise<void> {
    try {
      const [orgTypesRes, subTypesRes] = await Promise.all([
        this.healthSystemService.getHealthOrganizationTypes().toPromise(),
        this.healthSystemService.getHealthOrganizationSubscriptionTypes().toPromise()
      ]);

      if (orgTypesRes.resultStatus.isSuccess) {
        this.healthOrgTypes = orgTypesRes.result;
        if (this.signupType && this.healthOrgTypes.length) {
          const selectedSignupType = this.healthOrgTypes.find(x => x.id === Number(this.signupType));
          this.organizationForm.controls.organizationType.setValue(selectedSignupType.id);
          this.organizationForm.controls.organizationName.setValue(selectedSignupType.description);
        }
      }

      if (subTypesRes.resultStatus.isSuccess) {
        this.healthOrgSubTypes = subTypesRes.result;
        this.isDataLoaded = true;
        this.subscriptionForm.get('subscriptionType').setValue(this.healthOrgSubTypes[0].id);
      }
    } catch (error) {
      console.error(error);
    }
  }

  onSubscriptionTypeSelect(id: number) {
    this.subscriptionForm.controls['subscriptionType'].setValue(id);
  }

  getOrgTypeDisplayValue(orgType) {
    let orgTypeName = orgType.name;
    let orgTypeDesc = orgType.description;

    if ((orgTypeDesc != null && orgTypeDesc != '') && orgTypeName.toLowerCase() != orgTypeDesc.toLowerCase()) {
      return `${orgTypeName} (${orgTypeDesc})`;
    } else {
      return orgTypeName;
    }
  }

  async onFormSubmit(): Promise<void> {
    if (this.organizationForm.valid && this.contactForm.valid && this.subscriptionForm.valid) {
      this.isFormSubmitting = true;
      const fullName = `${this.organizationForm.controls.firstName.value} ${this.organizationForm.controls.middleInitial.value} ${this.organizationForm.controls.lastName.value}, ${this.organizationForm.controls.suffix.value}`;
      const formData = new FormData();

      if (this.isBrokerAgent) {
        formData.append("Name", this.organizationForm.controls.name.value);
      } else {
        formData.append("Name", this.organizationForm.controls.npnNumber.value);
      }
      formData.append("IsCertify", this.contactForm.controls.isCertify.value);
      formData.append("IsAcceptedTermsConditions", this.contactForm.controls.saaSAgreement.value);
      formData.append("IsAcceptedBaa", this.contactForm.controls.isAcceptedBaa.value);
      formData.append("WebAddress", this.organizationForm.controls.webAddress.value);
      formData.append("PhoneNumber", this.organizationForm.controls.phoneNumber.value);
      formData.append("NetworkProvider", this.organizationForm.controls.networkProvider.value);
      formData.append("Line", this.organizationForm.controls.primaryAddress.value);
      formData.append("Line2", this.organizationForm.controls.buildingSuite.value);
      formData.append("City", this.organizationForm.controls.city.value);
      formData.append("State", this.organizationForm.controls.state.value);
      formData.append("ZipCode", this.organizationForm.controls.zip.value);
      formData.append("AdminName", fullName);
      formData.append("AdminEmail", this.organizationForm.controls.email.value);
      formData.append("HealthOrganizationType", this.organizationForm.controls.organizationType.value);
      formData.append("MoneyBackGuarantee", this.contactForm.controls.moneyBackGuarantee.value || false);
      formData.append("CreditCardDisclaimer", this.contactForm.controls.creditCardDisclaimer.value || false);
      formData.append("CancelationPolicy", this.contactForm.controls.cancelationPolicy.value || false);
      formData.append("NoticeWebsitePrivacyPractices", this.contactForm.controls.noticeWebsitePrivacyPractices.value || false);
      formData.append("CopyrightNotice", this.contactForm.controls.copyrightNotice.value || false);
      formData.append("PrivacyPolicy", this.contactForm.controls.privacyPolicy.value || false);
      formData.append("MedicalInformationDisclaimer", this.contactForm.controls.medicalInformationDisclaimer.value || false);
      formData.append("NoticeHealthcarePractices", this.contactForm.controls.noticeHealthcarePractices.value || false);
      formData.append("PrivacySecurityElectronicHR", this.contactForm.controls.privacySecurityElectronicHR.value || false);
      formData.append("SubscriptionTypeId", this.subscriptionForm.controls.subscriptionType.value);

      try {
        const res = await this.healthSystemService.organizationSignup(formData).toPromise();
        if (res && res.isSuccess) {
          this.document.location.href = "https://appyhealth.com/";
        }
        this.isFormSubmitting = false;
      } catch (error) {
        this.isFormSubmitting = false;
        console.error(error);
      }
    }
  }
  async onFormSubmitEmployer(): Promise<void> {
    if (this.organizationForm.valid &&  this.subscriptionForm.valid) {
      this.isFormSubmitting = true;
      const fullName = `${this.organizationForm.controls.firstName.value} ${this.organizationForm.controls.middleInitial.value} ${this.organizationForm.controls.lastName.value}, ${this.organizationForm.controls.suffix.value}`;
      const formData = new FormData();

      if (this.isEmployer) {
        formData.append("Name", this.organizationForm.controls.name.value);
      } else {
        formData.append("Name", this.organizationForm.controls.npnNumber.value);
      }
      formData.append("IsCertify", this.contactForm.controls.isCertify.value);
      formData.append("IsAcceptedTermsConditions", this.contactForm.controls.saaSAgreement.value);
      formData.append("IsAcceptedBaa", this.contactForm.controls.isAcceptedBaa.value);
      formData.append("WebAddress", this.organizationForm.controls.webAddress.value);
      formData.append("PhoneNumber", this.organizationForm.controls.phoneNumber.value);
      formData.append("NetworkProvider", this.organizationForm.controls.networkProvider.value);
      formData.append("Line", this.organizationForm.controls.primaryAddress.value);
      formData.append("Line2", this.organizationForm.controls.buildingSuite.value);
      formData.append("City", this.organizationForm.controls.city.value);
      formData.append("State", this.organizationForm.controls.state.value);
      formData.append("ZipCode", this.organizationForm.controls.zip.value);
      formData.append("AdminName", fullName);
      formData.append("AdminEmail", this.organizationForm.controls.email.value);
      formData.append("HealthOrganizationType", this.organizationForm.controls.organizationType.value);
      formData.append("MoneyBackGuarantee", this.contactForm.controls.moneyBackGuarantee.value || false);
      formData.append("CreditCardDisclaimer", this.contactForm.controls.creditCardDisclaimer.value || false);
      formData.append("CancelationPolicy", this.contactForm.controls.cancelationPolicy.value || false);
      formData.append("NoticeWebsitePrivacyPractices", this.contactForm.controls.noticeWebsitePrivacyPractices.value || false);
      formData.append("CopyrightNotice", this.contactForm.controls.copyrightNotice.value || false);
      formData.append("PrivacyPolicy", this.contactForm.controls.privacyPolicy.value || false);
      formData.append("MedicalInformationDisclaimer", this.contactForm.controls.medicalInformationDisclaimer.value || false);
      formData.append("NoticeHealthcarePractices", this.contactForm.controls.noticeHealthcarePractices.value || false);
      formData.append("PrivacySecurityElectronicHR", this.contactForm.controls.privacySecurityElectronicHR.value || false);
      formData.append("SubscriptionTypeId", this.subscriptionForm.controls.subscriptionType.value);

      try {
        const res = await this.healthSystemService.organizationSignup(formData).toPromise();
        if (res && res.isSuccess) {
          this.document.location.href = "https://appyhealth.com/";
        }
        this.isFormSubmitting = false;
      } catch (error) {
        this.isFormSubmitting = false;
        console.error(error);
      }
    }
  }
  submitFromStep(form: FormGroup): void {
    if (form.valid) {
      this.nextStep();
    } else {
      this.markFormFieldsAsTouched(form);
    }
  }

  private markFormFieldsAsTouched(form: FormGroup): void {
    form.markAllAsTouched();
  }

  organizationSubmit(): void {
    // this.submitFromStep(this.organizationForm);
    if(this.isBrokerAgent){
      this.submitFromStep(this.organizationForm);
    } else {
      this.submitFromStep(this.organizationForm);

    }

  }

  contactSubmit(): void {
    if (this.areAllCheckboxesChecked()) {
      this.nextStep(); // Proceed to the next step if all checkboxes are checked
    } else {
      this.contactForm.markAllAsTouched(); // Highlight invalid fields
    }
  }
  toggleAllCheckboxes(checked: boolean): void {
    this.contactForm.patchValue({
      saaSAgreement: checked,
      isAcceptedBaa: checked,
      isCertify: checked
    });
  }

  areAllCheckboxesChecked(): boolean {
    const saaSAgreement = this.contactForm.get('saaSAgreement');
    const isAcceptedBaa = this.contactForm.get('isAcceptedBaa');
    const isCertify = this.contactForm.get('isCertify');

    return (saaSAgreement && saaSAgreement.value) &&
           (isAcceptedBaa && isAcceptedBaa.value) &&
           (isCertify && isCertify.value);
  }

  nextStep(): void {
    if (this.stepNo < 2) {
      this.stepNo += 1;
    }

    if (this.stepNo === 1) {
      this.isFormSubmitting = false;
      this.isDisplayThankYou = false;

      // Show Thank You for Employer specifically at step 1
      if (this.isEmployer) {
        this.isDisplayThankYou = true;
        this.subscriptionForm.get('subscriptionType').setValue(2);
      }
    } else if (this.stepNo === 2) {
      this.isFormSubmitting = false;
      this.isDisplayThankYou = false;

      // Show Thank You for Broker Agent at step 2
      if (this.isBrokerAgent) {
        this.isDisplayThankYou = true;
        this.subscriptionForm.get('subscriptionType').setValue(1);
      }
}
  }


backToEdit(): void {
    if (this.stepNo > 0) {
      this.stepNo -= 1;
    }
  }

  goToFinish(): void {
    this.isFormSubmitting = false;
    this.isDisplayThankYou = true;
  }

  numberOnly(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    return (charCode > 31 && (charCode < 48 || charCode > 57)) ? false : true;
  }

  getStatus(stepNo: number): boolean {
    return this.stepNo >= stepNo;
  }

  openDialog(dialogId: string, title: string, filename: string, isBack: boolean, setValueFn: (result: any) => void): void {
    const dialogRef = this.dialog.open(PDFDialogComponent, {
      width: '85%',
      id: dialogId,
      data: { title, filename, isBack }
    });
    dialogRef.afterClosed().subscribe(
      (result) => setValueFn(result),
      (error) => console.error(error)
    );
  }

  openBAADialog(): void {
    this.openDialog('baa_dialog', 'Appy Health Business Associate Agreement', 'AH–-Business-Associate-–-Healthcare-Provider-Agreement-–-November-2022-–-A7.pdf', true,
      (result) => this.contactForm.controls.isAcceptedBaa.setValue(result));
  }

  openSaaSAgreementDialog(): void {
    this.openDialog('sass_dialog', 'Appy Health Saas Agreement', 'AH-HPA-SaaS-Agreement-December-2024-v6.pdf', true,
      (result) => this.contactForm.controls.saaSAgreement.setValue(result));
  }

  openPdfDialog(type: number): void {
    let title = 'Appy Health Pricing';
    let filename = 'AH-Practice-Clinic-Pricing-11-4-2022.pdf';

    if (type === 2) {
      title = 'Appy Health Money Back Guarantee';
      filename = 'AH-Money-Back-Guarantee-11.14.2022-D5.pdf';
    } else if (type === 3) {
      title = 'Appy Health Cancellation Policy';
      filename = 'AH-Cancellation-Policy-11.14.2022-D9.pdf';
    }

    this.openDialog('subscription_dialog', title, filename, false, () => { });
  }

  cancelSignup() {
    this.router.navigate(['/home']);
  }

}
